import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { useRef } from "react";
import Slider from "react-slick";
import { useNavigate } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { designTokens } from "../../../theme/designTokens";
import { heroSlides } from "../../../theme/homeContent";

const { colors, spacing } = designTokens;

const HeroSection = () => {
  const sliderRef = useRef<Slider>(null);
  const navigate = useNavigate();

  const settings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    arrows: false,
    fade: true,
    pauseOnHover: true,
  };

  return (
    <Box
      sx={{
        position: "relative",
        mt: `${spacing.headerHeight.xs}px`,
        "@media (min-width:900px)": { mt: `${spacing.headerHeight.md}px` },
        "& .slick-dots": { bottom: 16 },
        "& .slick-dots li button:before": { color: colors.white, opacity: 0.5, fontSize: 10 },
        "& .slick-dots li.slick-active button:before": { color: colors.primary, opacity: 1 },
      }}
    >
      <IconButton
        onClick={() => sliderRef.current?.slickPrev()}
        sx={{
          position: "absolute",
          left: { xs: 8, md: 24 },
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          color: colors.white,
          backgroundColor: "rgba(0,0,0,0.2)",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.4)" },
        }}
        aria-label="Previous slide"
      >
        <ChevronLeftIcon />
      </IconButton>

      <IconButton
        onClick={() => sliderRef.current?.slickNext()}
        sx={{
          position: "absolute",
          right: { xs: 8, md: 24 },
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          color: colors.white,
          backgroundColor: "rgba(0,0,0,0.2)",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.4)" },
        }}
        aria-label="Next slide"
      >
        <ChevronRightIcon />
      </IconButton>

      <Slider ref={sliderRef} {...settings}>
        {heroSlides.map((slide) => (
          <Box key={slide.id}>
            <Box
              sx={{
                position: "relative",
                height: spacing.heroHeight,
                backgroundImage: `url(${slide.background})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor:
                    slide.id === "s3" ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.45)",
                }}
              />
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  textAlign: "center",
                  px: { xs: 3, md: 6 },
                  maxWidth: 1000,
                  mx: "auto",
                }}
              >
                {slide.titleHighlight ? (
                  <Typography
                    component="h1"
                    sx={{
                      color: colors.white,
                      fontWeight: 700,
                      fontSize: designTokens.fontSizes.heroTitle,
                      lineHeight: 1.3,
                      mb: 2,
                    }}
                  >
                    {slide.title}
                    <br />
                    <Box component="span" sx={{ color: colors.primary }}>
                      {slide.titleHighlight}
                    </Box>
                  </Typography>
                ) : (
                  <Typography
                    component="h1"
                    sx={{
                      color: colors.white,
                      fontWeight: 700,
                      fontSize: designTokens.fontSizes.heroTitle,
                      lineHeight: 1.3,
                      mb: 2,
                    }}
                  >
                    {slide.title}
                  </Typography>
                )}

                <Typography
                  sx={{
                    color:
                      slide.subtitleColor === "primary"
                        ? colors.primary
                        : colors.white,
                    fontSize: { xs: "1rem", md: "1.25rem" },
                    fontWeight: slide.subtitleColor === "primary" ? 600 : 500,
                    lineHeight: 1.6,
                    mb: slide.showCtas ? 4 : 0,
                    maxWidth: 900,
                    mx: "auto",
                    ...(slide.id === "s3" && {
                      backgroundColor: colors.heroOverlay,
                      p: 2,
                      borderRadius: designTokens.borderRadius.sm,
                    }),
                  }}
                >
                  {slide.subtitle}
                </Typography>

                {slide.showCtas && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                      onClick={() => navigate("/offering")}
                      sx={{
                        px: 3,
                        py: 1.25,
                        minWidth: 212,
                        letterSpacing: 1,
                      }}
                    >
                      Explore Services
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/contract-management")}
                      sx={{
                        px: 3,
                        py: 1.25,
                        minWidth: 212,
                        letterSpacing: 1,
                        color: colors.white,
                        borderColor: colors.white,
                        borderWidth: 2,
                        "&:hover": {
                          borderColor: colors.white,
                          borderWidth: 2,
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      Our Products
                    </Button>
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Slider>
    </Box>
  );
};

export default HeroSection;
